import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, RefreshCw, Inbox, PackageCheck, Clock } from 'lucide-react';

const DEPT_LABELS = { diamond: 'Diamond', stone: 'Colour Stone', gold: 'Gold' };
const STATUS_STYLE = {
  pending: 'bg-red-100 text-red-700',
  available: 'bg-green-100 text-green-700',
  arrange: 'bg-amber-100 text-amber-700',
  not_available: 'bg-gray-200 text-gray-600',
};

const MaterialConfirmations = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await api.get('/orders/pending-feasibility');
      setOrders(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchList(); }, []);

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="material-confirmations-page">
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">Material Confirmations</h1>
              <p className="text-xs text-emerald-200">{orders.length} order(s) awaiting your confirmation</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={fetchList}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6">
        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Inbox className="w-14 h-14 mx-auto mb-3 text-gray-300" />
            <p className="font-semibold text-gray-600">All caught up</p>
            <p className="text-sm text-gray-400 mt-1">No orders waiting for your material confirmation</p>
          </div>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => {
              const responses = o.material_feasibility?.responses || {};
              return (
                <div
                  key={o._id}
                  onClick={() => navigate('/messenger', { state: { selectOrder: o._id } })}
                  className="rounded-lg border border-gray-200 bg-white p-3 cursor-pointer hover:shadow-md transition-shadow"
                  data-testid={`material-confirm-order-${o.order_number}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <PackageCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span className="font-bold text-sm text-gray-900">{o.order_number}</span>
                        {o.main_group && <span className="text-[10px] text-gray-400">{o.main_group}</span>}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5 truncate">{o.title}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {Object.entries(responses).map(([dept, r]) => (
                          <span key={dept} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_STYLE[r?.status] || 'bg-gray-100 text-gray-500'}`}>
                            {DEPT_LABELS[dept] || dept}: {r?.status === 'not_available' ? 'N/A' : (r?.status || 'pending')}
                          </span>
                        ))}
                      </div>
                    </div>
                    {o.delivery_date && (
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5 flex-shrink-0"><Clock className="w-3 h-3" />{o.delivery_date}</span>
                    )}
                  </div>
                  <p className="text-[11px] text-emerald-600 font-medium mt-2">Tap to open order &amp; confirm →</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialConfirmations;
